import { Box, Href, Text } from '@factorialco/deprecated-design-system'
import { Form } from 'factorial-form'
import { ContractsCustomFieldsPoliciesFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ContractFlowType } from 'modules/contracts/components/FlowPage'
import FormWrapper from 'modules/contracts/components/FlowPage/FormWrapper'
import Hint from 'modules/contracts/components/FlowPage/FormWrapper/Hint'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import { getCustomFieldKey } from 'modules/contracts/lib/customFields'

import BlankSlate from './BlankSlate'
import CustomField from './CustomField'

interface Props {
  form: Form
  translations: { [key: string]: string }
  step?: number
  type: ContractFlowType
  withFormWrapper?: boolean
  customFieldsContextlessPolicies: ContractsCustomFieldsPoliciesFragment | undefined
  legalEntityId: number
}

interface CustomFieldsHintProps {
  legalEntityId: number
  step: number
  type: ContractFlowType
}

const CustomFieldsHint = ({ legalEntityId, step, type }: CustomFieldsHintProps) => {
  const { navigation } = useNavigation()

  const trackLink = () => {
    track(trackingEvents.employee.contracts.goToCustomFields, {
      step: step,
      type: type,
    })
  }

  return (
    <Hint color="grey200">
      <Href
        onClick={() => trackLink()}
        href={navigation.settings.legalEntitiesId.contracts.fullPath({ id: legalEntityId })}
        target="_blank"
      >
        <Text.Normal color="grey1000" weight="normal" size="100" underlined>
          {i18n.t('contract_versions.flows.fix.steps.legal_and_extras.hints.custom_fields')}
        </Text.Normal>
      </Href>
    </Hint>
  )
}

const CustomFields = ({
  form,
  step,
  type,
  translations,
  withFormWrapper,
  customFieldsContextlessPolicies,
  legalEntityId,
}: Props) => {
  const { customFields } = useContractFlow()

  const canCreateCustomFields =
    customFieldsContextlessPolicies?.companyCreateCustomFields.can ?? false
  const emptyList = !canCreateCustomFields && customFields?.length === 0

  if (emptyList) return null

  if (!customFields || customFields.length === 0) {
    return (
      <FormWrapper
        withFormWrapper={withFormWrapper}
        hint={step && <CustomFieldsHint legalEntityId={legalEntityId} step={step} type={type} />}
      >
        <BlankSlate translations={translations} />
      </FormWrapper>
    )
  }

  return (
    <Box paddingTop="s24">
      <FormWrapper
        withFormWrapper={withFormWrapper}
        hint={step && <CustomFieldsHint legalEntityId={legalEntityId} step={step} type={type} />}
      >
        <Box gap="s24" paddingBottom="s24">
          {customFields.map((field) => {
            return <CustomField key={getCustomFieldKey(field)} form={form} field={field} />
          })}
        </Box>
      </FormWrapper>
    </Box>
  )
}

export default observer(CustomFields)
